class HorizontalScroll extends HTMLElement {
    constructor() {
        super();
        const element = this.querySelector("[data-horizontal-scrollable]");
        this.showScrollButtons =
            this.getAttribute("show-scroll-buttons") !== "false";
        this.customBackgroundColor = this.getAttribute(
            "custom-background-color",
        );
        this.scrollEffectLeft = false;
        this.scrollEffectRight = false;
        this.scrollLeftButton = null;
        this.scrollRightButton = null;
        this.scrollableElement = element;
        this.isDown = false;
        this.hasDragged = false;
        this.dragThreshold = 5;
        this.velocity = 0;
        this.currentScrollLeft = null;
        this.startX = null;
        this.lastX = this.startX;
        this.settings = this._parseSettings(this.getAttribute("data-settings"));
        this._scrollFrame = null;
        this._handlers = {};
        this._buttonHandlers = {};
        this._resizeObserver = null;
        this._decelerationFrame = null;
        this._onColorItemChange = (event) => this.onColorItemChange(event);
    }

    _parseSettings(raw) {
        if (!raw) return null;
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }
    connectedCallback() {
        if (!this.scrollableElement) {
            this.scrollableElement = this.querySelector(
                "[data-horizontal-scrollable]",
            );
        }

        if (this.scrollableElement) {
            this.attachListeners();
            this.checkIsOverFlow();
            this.renderScrollButtons();
            this.handleCheckResize();
        }
        document.addEventListener(
            "on:color-item:change",
            this._onColorItemChange,
        );
    }

    disconnectedCallback() {
        this.detachListeners();
        document.removeEventListener(
            "on:color-item:change",
            this._onColorItemChange,
        );
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
        if (this._decelerationFrame) {
            cancelAnimationFrame(this._decelerationFrame);
            this._decelerationFrame = null;
        }
        if (this._scrollFrame) {
            cancelAnimationFrame(this._scrollFrame);
            this._scrollFrame = null;
        }
    }

    detachListeners() {
        if (this.scrollableElement) {
            for (const [event, handler] of Object.entries(this._handlers)) {
                this.scrollableElement.removeEventListener(event, handler);
            }
        }
        this._handlers = {};

        if (this.scrollLeftButton && this._buttonHandlers.left) {
            this.scrollLeftButton.removeEventListener(
                "click",
                this._buttonHandlers.left,
            );
        }
        if (this.scrollRightButton && this._buttonHandlers.right) {
            this.scrollRightButton.removeEventListener(
                "click",
                this._buttonHandlers.right,
            );
        }
        this._buttonHandlers = {};
    }

    checkIsOverFlow() {
        if (
            this.scrollableElement.scrollWidth <=
            this.scrollableElement.clientWidth
        ) {
            if (this.scrollEffectRight) {
                this.scrollEffectRight.style.opacity = "0";
                this.scrollEffectRight.style.visibility = "hidden";
            }

            if (this.scrollRightButton) {
                this.scrollRightButton.style.opacity = "0";
                this.scrollRightButton.style.visibility = "hidden";
            }
        } else {
            if (this.scrollableElement.scrollLeft > 0 && this.scrollLeftButton) {
                this.scrollLeftButton.style.opacity = "1";
                this.scrollLeftButton.style.visibility = "visible";
            }
            const totalScroll =
                this.scrollableElement.scrollLeft +
                this.scrollableElement.clientWidth;
            if (this.scrollRightButton) {
                this.scrollRightButton.style.opacity =
                    totalScroll < this.scrollableElement.scrollWidth
                        ? "1"
                        : "0";
                this.scrollRightButton.style.visibility =
                    totalScroll < this.scrollableElement.scrollWidth
                        ? "visible"
                        : "hidden";
            }
        }
    }

    attachListeners() {
        this._handlers.mousedown = (e) => {
            if (this._decelerationFrame) {
                cancelAnimationFrame(this._decelerationFrame);
                this._decelerationFrame = null;
            }
            this.isDown = true;
            this.hasDragged = false;
            this.startX = e.pageX - this.scrollableElement.offsetLeft;
            this.currentScrollLeft = this.scrollableElement.scrollLeft;
            this.velocity = 0;
            this.lastX = this.startX;
        };

        this._handlers.mouseup = () => {
            if (!this.isDown) return;
            this.isDown = false;

            if (this.hasDragged) {
                this.scrollableElement.classList.remove("dragging");
                this.handleSmoothDeceleration();
            }
        };

        this._handlers.mousemove = (e) => {
            if (!this.isDown) return;

            const x = e.pageX - this.scrollableElement.offsetLeft;
            const walk = x - this.startX;

            // Threshold: only engage drag mode after crossing dragThreshold px.
            // Prevents .dragging (pointer-events:none) from eating genuine clicks.
            if (!this.hasDragged) {
                if (Math.abs(walk) < this.dragThreshold) return;
                this.hasDragged = true;
                this.scrollableElement.classList.add("dragging");
            }

            e.preventDefault();
            this.scrollableElement.scrollLeft = this.currentScrollLeft - walk;

            this.velocity = this.lastX - x;
            this.lastX = x;
        };

        this._handlers.mouseleave = () => {
            if (!this.isDown) return;
            this.isDown = false;

            if (this.hasDragged) {
                this.scrollableElement.classList.remove("dragging");
                this.handleSmoothDeceleration();
            }
        };

        this._handlers.scroll = () => {
            if (this._scrollFrame) return;
            this._scrollFrame = requestAnimationFrame(() => {
                this._scrollFrame = null;
                this._updateScrollIndicators();
            });
        };

        for (const [event, handler] of Object.entries(this._handlers)) {
            this.scrollableElement.addEventListener(event, handler);
        }
    }

    handleScrollButtons(leftButton, rightButton) {
        if (leftButton) {
            this._buttonHandlers.left = (e) => {
                e.preventDefault();
                if (
                    this.scrollableElement.scrollLeft -
                        this.scrollableElement.clientWidth <=
                    0
                ) {
                    this.scrollableElement.style.scrollBehavior = "smooth";
                    this.scrollableElement.scrollLeft = 0;
                    this.scrollableElement.style.scrollBehavior = "auto";
                } else {
                    this.scrollableElement.style.scrollBehavior = "smooth";
                    this.scrollableElement.scrollLeft -=
                        this.scrollableElement.clientWidth;
                    this.scrollableElement.style.scrollBehavior = "auto";
                }
            };
            leftButton.addEventListener("click", this._buttonHandlers.left);
        }

        if (rightButton) {
            this._buttonHandlers.right = (e) => {
                e.preventDefault();
                if (
                    this.scrollableElement.scrollLeft +
                        this.scrollableElement.clientWidth >=
                    this.scrollableElement.scrollWidth
                ) {
                    this.scrollableElement.style.scrollBehavior = "smooth";
                    this.scrollableElement.scrollLeft =
                        this.scrollableElement.scrollWidth;
                    this.scrollableElement.style.scrollBehavior = "auto";
                } else {
                    this.scrollableElement.style.scrollBehavior = "smooth";
                    this.scrollableElement.scrollLeft +=
                        this.scrollableElement.clientWidth;
                    this.scrollableElement.style.scrollBehavior = "auto";
                }
            };
            rightButton.addEventListener("click", this._buttonHandlers.right);
        }
    }

    renderScrollButtons() {
        if (!this.showScrollButtons) return;

        // Idempotent: buttons still in the DOM → just refresh visibility.
        // If missing (e.g. removed by a morph pass on color change), fall through and recreate.
        if (
            this.scrollLeftButton &&
            this.scrollRightButton &&
            this.contains(this.scrollLeftButton) &&
            this.contains(this.scrollRightButton)
        ) {
            this.checkIsOverFlow();
            return;
        }

        this.scrollLeftButton = null;
        this.scrollRightButton = null;
        this._buttonHandlers = {};

        const leftEffect = document.createElement("div");
        const rightEffect = document.createElement("div");
        const leftButton = document.createElement("button");
        const rightButton = document.createElement("button");
        leftButton.type = "button";
        rightButton.type = "button";
        leftButton.setAttribute("aria-label", "Scroll left");
        rightButton.setAttribute("aria-label", "Scroll right");
        leftEffect.classList.add("scroll-before");
        rightEffect.classList.add("scroll-after");
        leftButton.classList.add("scroll-left");
        rightButton.classList.add("scroll-right");
        leftButton.innerHTML =
            '<svg class="Icon Icon--select-arrow-left" role="presentation" viewBox="0 0 11 18"><path d="M9.5 1.5L1.5 9l8 7.5" stroke-width="2" stroke="currentColor" fill="none" fill-rule="evenodd" stroke-linecap="square"></path></svg>';
        rightButton.innerHTML =
            '<svg class="Icon Icon--select-arrow-right" role="presentation" viewBox="0 0 11 18"><path d="M1.5 1.5l8 7.5-8 7.5" stroke-width="2" stroke="currentColor" fill="none" fill-rule="evenodd" stroke-linecap="square"></path></svg>';
        this.scrollLeftButton = leftButton;
        this.scrollRightButton = rightButton;

        if (this.settings?.buttonWidth) {
            leftButton.style.padding = `${this.settings.buttonWidth}px`;
            rightButton.style.padding = `${this.settings.buttonWidth}px`;
            leftButton.querySelector("svg").style.width =
                `${this.settings.buttonWidth}px`;
            leftButton.querySelector("svg").style.height =
                `${this.settings.buttonWidth}px`;
            rightButton.querySelector("svg").style.width =
                `${this.settings.buttonWidth}px`;
            rightButton.querySelector("svg").style.height =
                `${this.settings.buttonWidth}px`;
        }
        this.append(leftButton, rightButton);
        this.handleScrollButtons(leftButton, rightButton);
        this.checkIsOverFlow();
    }

    _updateScrollIndicators() {
        const currentScroll = Math.ceil(this.scrollableElement.scrollLeft);
        const atStart = currentScroll <= 0;
        const atEnd =
            currentScroll >=
            this.scrollableElement.scrollWidth -
                this.scrollableElement.offsetWidth;

        this._toggleVisibility(this.scrollEffectLeft, !atStart);
        this._toggleVisibility(this.scrollLeftButton, !atStart);
        this._toggleVisibility(this.scrollEffectRight, !atEnd);
        this._toggleVisibility(this.scrollRightButton, !atEnd);
    }

    _toggleVisibility(element, visible) {
        if (!element) return;
        element.style.opacity = visible ? "1" : "0";
        element.style.visibility = visible ? "visible" : "hidden";
    }

    handleCheckResize() {
        this._resizeObserver?.disconnect();
        this._resizeObserver = new ResizeObserver(() => {
            this.checkIsOverFlow();
        });
        this._resizeObserver.observe(this);
    }

    handleSmoothDeceleration() {
        if (Math.abs(this.velocity) < 0.1) {
            this.velocity = 0;
            this._decelerationFrame = null;
            return;
        }

        this.scrollableElement.scrollLeft += this.velocity;
        this.velocity *= 0.85;

        this._decelerationFrame = requestAnimationFrame(() =>
            this.handleSmoothDeceleration(),
        );
    }

    onColorItemChange(event) {
        if (event.detail.productHandle) {
            // After color morph, scroll buttons may have been removed from DOM.
            // renderScrollButtons is idempotent when they're still attached.
            this.renderScrollButtons();
        }
    }
}

customElements.define("horizontal-scroll", HorizontalScroll);
